import { CONCEPT_ICONS } from "@/components/ui/concept-icons";
import {
  useCallback,
  type CSSProperties,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { replyAwaitingInput } from "@/api/awaiting-input";
import { resolveCursorQuestion } from "@/lib/resolve-cursor-question";
import {
  captureContextualDecisionSubmission,
  recordContextualTaskAcceptance,
} from "@/desktop/onboarding/contextual-task-acceptance";
import { Button, TextInput } from "@/components/ui";
import { cn } from "@/lib/cn";
import { useT } from "@/i18n";
import { cursorsMessages } from "@/i18n/messages/cursors";
import { computePlacement, type Rect } from "@/lib/edge-aware-placement";
import { measureUntransformed } from "@/lib/measure-untransformed";
import { useProjectStore } from "@/stores/project-store";
import { useDictation } from "@/desktop/use-dictation";
import { useCommentDictationClaim } from "@/desktop/use-comment-dictation-claim";
import { surfaceRoleAttributes } from "@/components/ui/surface-contract";

export type AgentCursorQuestionAppearance = "glass" | "embed";

export interface AgentCursorQuestionData {
  awaitingInputId: string;
  question: string;
  options: Array<{ id: string; label: string }>;
}

interface AgentCursorQuestionCardProps {
  question: AgentCursorQuestionData;
  agentName: string;
  agentTextShadow: string;
  bubbleShell: string;
  bubbleStyle: CSSProperties;
  testId: string;
  edgeAware?: boolean;
  className?: string;
  exclusions?: Rect[];
  /** False when an outer header already shows the agent name (e.g. the
   *  compact widget's single-agent panel) — showing it again here just
   *  repeats it, so the label reads "Question" instead. Defaults to true
   *  for the standalone on-canvas cursor bubble, which has no other header
   *  and needs the name for attribution. */
  showAgentName?: boolean;
  /** Called after the reply is accepted, e.g. to refresh the owner's state. */
  onAnswered?: () => void;
  /** Optional reply owner for input-bar question mode. */
  onReply?: (answer: string, selectedOption: string) => Promise<boolean>;
  /** "embed" draws the card like an inline frame card in a conversation
   *  thread: outlined content on the host's glass with no glass surface of
   *  its own, so the thread clips it like any other content. */
  appearance?: AgentCursorQuestionAppearance;
}

export function AgentCursorQuestionCard({
  question,
  agentName,
  agentTextShadow,
  bubbleShell,
  bubbleStyle,
  testId,
  edgeAware = false,
  className,
  exclusions,
  showAgentName = true,
  onAnswered,
  onReply,
  appearance = "glass",
}: AgentCursorQuestionCardProps) {
  const t = useT(cursorsMessages);
  const projectId = useProjectStore((s) => s.currentProject?.id);
  const [pendingOptionId, setPendingOptionId] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);
  const [customAnswer, setCustomAnswer] = useState("");
  const dictationStatus = useDictation((s) => s.status);
  const isBusy = pendingOptionId !== null || sent;
  const resetTimerRef = useRef<number | null>(null);

  const glass = appearance === "glass";
  const glassSurface = glass
    ? surfaceRoleAttributes("menu", {
        nativeLevel: "root",
        nativeGroup: "menus",
      })
    : {};
  const cardClassName = glass ? "denker-agent-question-card" : "text-primary";
  const optionClassName = glass
    ? "denker-agent-question-option"
    : "text-primary hover:bg-[var(--color-interactive-hover)]";

  const ref = useRef<HTMLDivElement>(null);
  const [translate, setTranslate] = useState<{ x: number; y: number }>({
    x: 0,
    y: 0,
  });

  useLayoutEffect(() => {
    if (!edgeAware) {
      setTranslate({ x: 0, y: 0 });
      return;
    }
    const el = ref.current;
    if (!el) return;
    const measure = () => {
      const box = measureUntransformed(el);
      const pane =
        (el.closest("[data-canvas-viewport]") as HTMLElement | null) ??
        document.documentElement;
      const v = pane.getBoundingClientRect();
      const result = computePlacement({
        anchor: { x: box.left, y: box.top },
        size: { width: box.width, height: box.height },
        viewport: {
          left: v.left,
          top: v.top,
          right: v.right,
          bottom: v.bottom,
        },
        inset: 8,
        anchorOffset: 0,
        exclusions,
      });
      setTranslate({ x: result.left - box.left, y: result.top - box.top });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [edgeAware, question.options?.length, sent, customOpen, exclusions]);

  const schedulePendingReset = useCallback(() => {
    if (resetTimerRef.current !== null) {
      window.clearTimeout(resetTimerRef.current);
    }
    resetTimerRef.current = window.setTimeout(() => {
      setPendingOptionId(null);
      resetTimerRef.current = null;
    }, 800);
  }, []);

  const submitAnswer = useCallback(
    async (answer: string, selectedOption: string) => {
      if (isBusy) return;
      setPendingOptionId(selectedOption);
      captureContextualDecisionSubmission(question.awaitingInputId);
      try {
        if (onReply) {
          if (!(await onReply(answer, selectedOption))) {
            setPendingOptionId(null);
            return;
          }
        } else {
          if (!projectId) {
            setPendingOptionId(null);
            return;
          }
          const receipt = await replyAwaitingInput(
            projectId,
            question.awaitingInputId,
            {
              answer,
              selectedOption,
            },
          );
          if (receipt)
            recordContextualTaskAcceptance({
              status: receipt.status,
              project_id: projectId,
              execution_id: receipt.execution,
              conversation_id: receipt.conversation_id,
              accepted_task_id: receipt.accepted_task_id,
              saved_task_ids: receipt.saved_task_ids,
              selected_option: receipt.selected_option,
              answered_at: receipt.answered_at,
              answer_delivery_status: receipt.answer_delivery_status,
            });
        }
        setSent(true);
        resolveCursorQuestion(question.awaitingInputId);
        schedulePendingReset();
        onAnswered?.();
      } catch {
        setPendingOptionId(null);
      }
    },
    [
      isBusy,
      projectId,
      question.awaitingInputId,
      schedulePendingReset,
      onAnswered,
      onReply,
    ],
  );

  const answerDictation = useCommentDictationClaim({
    id: `awaiting-input:${question.awaitingInputId}`,
    deliverTranscript: (text) => {
      const answer = text.trim();
      if (!answer) return;
      void submitAnswer(answer, "other");
    },
  });

  useEffect(() => {
    return () => {
      if (resetTimerRef.current !== null) {
        window.clearTimeout(resetTimerRef.current);
      }
    };
  }, []);

  const handleOptionClick = (option: { id: string; label: string }) => {
    void submitAnswer(option.label, option.id);
  };

  const handleCustomSubmit = () => {
    const answer = customAnswer.trim();
    if (!answer) return;
    void submitAnswer(answer, "other");
  };

  return (
    <div
      ref={ref}
      className={cn(
        "pointer-events-auto flex w-max max-w-[360px] shrink-0 flex-col gap-1.5",
        className,
      )}
      style={{ transform: `translate(${translate.x}px, ${translate.y}px)` }}
      data-interactive="true"
      data-testid={`${testId}-question`}
      onPointerDown={(e) => e.stopPropagation()}
    >
      <div
        className={cn(
          cardClassName,
          "border px-3 py-2",
          "whitespace-pre-wrap break-words leading-relaxed text-appkit-caption",
          "font-medium",
          bubbleShell,
        )}
        style={bubbleStyle}
        {...glassSurface}
      >
        <div
          className="mb-1 text-appkit-mini font-semibold leading-none text-[var(--agent-cursor-name-color)]"
          style={{ textShadow: agentTextShadow }}
          data-testid={`${testId}-name`}
        >
          {showAgentName ? agentName : t("question")}
        </div>
        {question.question}
      </div>
      {sent ? (
        <div
          className={cn(
            cardClassName,
            "border px-3 py-2 text-appkit-caption leading-relaxed",
            !glass && "text-muted",
            bubbleShell,
          )}
          style={bubbleStyle}
          data-question-tone="muted"
          data-testid={`${testId}-question-sent`}
          {...glassSurface}
        >
          {t("answerSent")}
        </div>
      ) : (
        <>
          <div className="flex flex-wrap gap-1.5">
            {(question.options ?? []).slice(0, 4).map((option) => (
              <Button
                key={option.id}
                type="button"
                size="sm"
                variant="ghost"
                disabled={isBusy}
                onClick={() => handleOptionClick(option)}
                className={cn(
                  optionClassName,
                  "h-auto px-3 py-1.5 text-appkit-caption",
                  "whitespace-normal leading-relaxed",
                  bubbleShell,
                )}
                style={bubbleStyle}
                data-testid={`${testId}-question-option-${option.id}`}
                {...glassSurface}
              >
                {option.label}
              </Button>
            ))}
            <div
              className={cn(
                optionClassName,
                "flex items-center gap-1 border px-1 py-1",
                bubbleShell,
              )}
              style={bubbleStyle}
              data-testid={`${testId}-question-option-other`}
              {...glassSurface}
            >
              <span className="px-1 text-appkit-caption font-medium text-current opacity-70">
                {t("otherAnswer")}
              </span>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                disabled={isBusy || dictationStatus === "processing"}
                onClick={() => void answerDictation.toggle()}
                className="h-6 w-6 p-0"
                aria-label={t("answerByVoice")}
                data-testid={`${testId}-question-other-voice`}
              >
                <CONCEPT_ICONS.voice className="h-3 w-3" />
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                disabled={isBusy}
                onClick={() => setCustomOpen((v) => !v)}
                className="h-6 w-6 p-0"
                aria-label={t("typeAnotherAnswer")}
                data-testid={`${testId}-question-other-text`}
              >
                <CONCEPT_ICONS.chat className="h-3 w-3" />
              </Button>
            </div>
          </div>
          {customOpen && (
            <div className="flex w-full items-center gap-1.5">
              <TextInput
                value={customAnswer}
                onChange={(e) => setCustomAnswer(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleCustomSubmit();
                }}
                disabled={isBusy}
                placeholder={t("typeAnotherAnswer")}
                className="h-8 min-w-0 flex-1 text-appkit-caption"
                data-testid={`${testId}-question-other-input`}
              />
              <Button
                type="button"
                size="sm"
                disabled={isBusy || customAnswer.trim().length === 0}
                onClick={handleCustomSubmit}
                data-testid={`${testId}-question-other-submit`}
                className="shrink-0"
              >
                {t("send")}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
